import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GURUKULA, api, toEmbedUrl } from "../../lib/api";
import OrnamentalDivider from "./OrnamentalDivider";

export default function VisionMission() {
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get("/media?section=featured")
            .then((r) => {
                const vids = (Array.isArray(r.data) ? r.data : []).filter(
                    (m) => m.kind === "video" && m.url
                );
                setFeatured(vids);
            })
            .catch(() => setFeatured([]));
    }, []);

    const missions = [
        "To spread the light of music to every home and every heart.",
        "To preserve and promote the rich heritage of Indian classical music.",
        "To nurture students through the timeless Guru-Shishya tradition.",
        "To instill values, discipline, and cultural awareness alongside musical education.",
        "To create a community united through music, knowledge, and harmony.",
    ];

    return (
        <section
            id="about"
            data-testid="about-section"
            className="section-pad bg-cream"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-16">
                    <div className="eyebrow">· Welcome ·</div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-maroon mt-4">
                        A Sacred Space Where Music is Lived
                    </h2>
                    <p className="max-w-3xl mx-auto mt-6 text-ink/85 font-serif text-xl lg:text-2xl leading-relaxed">
                        Music is not merely an art form; it is a path to inner peace,
                        self-discovery and spiritual growth. At {GURUKULA.name}, we believe
                        music has the power to transform lives, refine character and
                        awaken the highest values within every individual.
                    </p>
                </div>

                <OrnamentalDivider className="mb-16" />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
                    {/* Vision */}
                    <div
                        className="lg:col-span-5 bg-white border border-gold-100 p-10 lg:p-12 card-cream"
                        data-testid="vision-card"
                    >
                        <div className="eyebrow text-gold">· Our Vision ·</div>
                        <h3 className="font-display text-3xl text-maroon mt-4 leading-tight">
                            A beacon of musical & cultural excellence.
                        </h3>
                        <p className="mt-6 font-serif text-lg text-ink/80 leading-relaxed">
                            To become a leading centre of musical and cultural excellence
                            that promotes universal brotherhood, spiritual awareness, and
                            human values through the transformative power of music.
                        </p>
                        <div className="mt-10 border-l-2 border-gold pl-5">
                            <p className="text-kannada text-lg text-maroon/90 italic">
                                ನಾದವೇ ದೈವ, ಸಂಗೀತವೇ ಸಾಧನೆ.
                            </p>
                            <p className="font-serif italic text-ink/70 mt-1">
                                Nada is Divine. Music is Sadhana.
                            </p>
                        </div>
                    </div>

                    {/* Mission */}
                    <div
                        className="lg:col-span-7 bg-maroon text-ivory p-10 lg:p-12 relative overflow-hidden"
                        data-testid="mission-card"
                    >
                        <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-gold/10 blur-2xl" />
                        <div className="eyebrow text-gold-300">· Our Mission ·</div>
                        <h3 className="font-display text-3xl text-ivory mt-4">
                            Five vows we live by every day.
                        </h3>
                        <ul className="mt-8 space-y-5">
                            {missions.map((m, idx) => (
                                <li key={idx} className="flex gap-4 items-start">
                                    <span className="text-gold-300 font-display text-xl leading-none mt-1">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                    <p className="font-serif text-lg text-ivory/90 leading-relaxed">
                                        {m}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Featured Performance */}
                <div className="mt-24 max-w-6xl mx-auto" data-testid="featured-video">
                    <motion.div
                        initial={{ opacity: 0, y: 48 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="eyebrow text-center mb-4">· Featured Performance ·</div>
                        <h3 className="font-display text-3xl lg:text-4xl text-maroon text-center leading-tight mb-10">
                            Performances from the
                            <span className="italic text-gold"> Gurukula stage.</span>
                        </h3>
                    </motion.div>

                    {featured.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, y: 48 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <div className="absolute -inset-10 bg-[radial-gradient(closest-side,rgba(181,138,67,0.22),transparent)] blur-2xl pointer-events-none" />
                            <div
                                data-testid="featured-video-player"
                                className="relative aspect-video border border-gold/50 bg-black overflow-hidden shadow-[0_60px_120px_-40px_rgba(54,27,30,0.55)]"
                            >
                                <iframe
                                    title="Shri Ananya Bhargava — Performance"
                                    src={GURUKULA.youtube}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="absolute inset-0 w-full h-full"
                                />
                            </div>
                        </motion.div>
                    ) : (
                        <div
                            data-testid="featured-video-grid"
                            className="grid grid-cols-1 md:grid-cols-2 gap-8"
                        >
                            {featured.slice(0, 10).map((m, i) => (
                                <motion.figure
                                    key={m.media_id}
                                    data-testid={`featured-video-item-${m.media_id}`}
                                    initial={{ opacity: 0, y: 32 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{
                                        duration: 0.7,
                                        delay: (i % 2) * 0.12,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    className="relative border border-gold-100 bg-white"
                                >
                                    <span className="absolute -top-3 left-5 z-10 bg-maroon text-gold-300 font-display text-sm px-3 py-1 tracking-widest">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="relative aspect-video bg-black">
                                        <iframe
                                            title={m.title || "Gurukula performance"}
                                            src={toEmbedUrl(m.url)}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            className="absolute inset-0 w-full h-full"
                                        />
                                    </div>
                                    {(m.title || m.caption) && (
                                        <figcaption className="p-5">
                                            {m.title && (
                                                <div className="font-display text-lg text-maroon leading-snug">
                                                    {m.title}
                                                </div>
                                            )}
                                            {m.caption && (
                                                <div className="font-serif italic text-ink/70 text-sm mt-1 leading-relaxed">
                                                    {m.caption}
                                                </div>
                                            )}
                                        </figcaption>
                                    )}
                                </motion.figure>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
